import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import type { ChecklistItem, SurveyAnswer } from '~/api/types'
import { QuestionCard } from './question-card'
import { useQuickSurvey } from './use-quick-survey'

export interface QuickSurveySheetProps {
    assessmentId: string
    checklist: ChecklistItem[] | null
    answers: SurveyAnswer[]
    /** 저장 성공 후 상위 답안 상태를 갱신하도록 알린다. */
    onSaved(): void
    /** 채점하기. */
    onScore(): void
    /** 채점 진행 중(상위 제어). */
    scoring?: boolean
}

/**
 * 빠른 설문지 — 전체 문항을 한 번에 깔고 칩/입력으로 빠르게 채운다.
 * 필드 단위로 즉시 saveAnswers(저장 표시 ✓) 하고, AI 설문과 동일한 값 형식을 쓴다.
 * 상태·저장·진행률 로직은 `useQuickSurvey` 훅이 보유한다.
 */
export function QuickSurveySheet({
    assessmentId,
    checklist,
    answers,
    onSaved,
    onScore,
    scoring,
}: QuickSurveySheetProps) {
    const {
        items,
        totalFields,
        answeredFields,
        percent,
        error,
        local,
        savingKeys,
        savedKeys,
        chooseChip,
        changeNumber,
    } = useQuickSurvey({ assessmentId, checklist, answers, onSaved })

    const ScoreButton = (
        <Button onClick={onScore} disabled={scoring}>
            {scoring ? '채점 중…' : '채점하기'}
        </Button>
    )

    return (
        <div className='flex h-full min-h-0 flex-col'>
            {/* 상단: 진행률 + 채점 */}
            <div className='border-border mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b pb-3'>
                <div className='min-w-[220px] flex-1'>
                    <div className='mb-1.5 flex items-center justify-between gap-2'>
                        <span className='text-foreground text-sm font-semibold'>
                            빠른 설문지
                        </span>
                        <span
                            className='text-muted-foreground text-xs'
                            aria-live='polite'>
                            {answeredFields}/{totalFields} 정보
                            {totalFields > 0 ? ` (${percent}%)` : ''}
                        </span>
                    </div>
                    <div
                        className='bg-muted h-2 w-full overflow-hidden rounded-full'
                        role='progressbar'
                        aria-valuemin={0}
                        aria-valuemax={totalFields}
                        aria-valuenow={answeredFields}
                        aria-label='빠른 설문지 진행도'>
                        <div
                            className='bg-primary h-full transition-all'
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                </div>
                {ScoreButton}
            </div>

            {error ? (
                <ErrorBanner className='mb-3 shrink-0'>{error}</ErrorBanner>
            ) : null}

            {/* 문항 카드 목록 */}
            <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
                {items.length === 0 ? (
                    <p className='text-muted-foreground text-sm'>
                        체크리스트를 불러오는 중이에요…
                    </p>
                ) : (
                    <ol className='space-y-3 pb-6'>
                        {items.map((item, index) => (
                            <QuestionCard
                                key={item.id}
                                item={item}
                                index={index}
                                local={local}
                                savingKeys={savingKeys}
                                savedKeys={savedKeys}
                                chooseChip={chooseChip}
                                changeNumber={changeNumber}
                            />
                        ))}
                    </ol>
                )}
            </div>

            {/* 하단: 진행률 + 채점 */}
            <div className='border-border mt-3 flex shrink-0 items-center justify-between gap-3 border-t pt-3'>
                <span className='text-muted-foreground text-sm'>
                    {answeredFields}/{totalFields} 정보 응답
                    {totalFields > 0 ? ` (${percent}%)` : ''}
                </span>
                {ScoreButton}
            </div>
        </div>
    )
}
